import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileText, Loader2, Phone, XCircle } from "lucide-react";
import { API, errMsg } from "@/lib/api";
import { da, fmtDate } from "@/lib/format";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const MSG: Record<string, string> = {
  accepte: "Merci ! Votre accord a bien été enregistré. Le garage va lancer les travaux.",
  refuse: "Votre refus a bien été enregistré. Le garage reviendra vers vous si besoin.",
  remplace: "Ce devis a été remplacé par une nouvelle version. Le garage va vous l'envoyer.",
  expire: "Ce devis a dépassé sa date de validité. Contactez le garage pour un nouveau devis.",
  brouillon: "Ce devis n'est pas encore disponible.",
};

function Lines({ q }: { q: any }) {
  return (
    <div className="mt-6 rounded-sm border border-border" data-testid="approval-lines">
      {q.lines.map((l: any, i: number) => (
        <div key={i} className="flex justify-between gap-4 border-b border-border px-4 py-2.5 text-sm last:border-0">
          <span>{l.label}</span><span className="font-mono-data text-muted-foreground">× {l.qty}</span>
        </div>
      ))}
      <div className="flex items-baseline justify-between bg-accent/40 px-4 py-3">
        <span className="eyebrow">{q.totals.tva ? "Total TTC" : "Total à payer"}</span>
        <span className="font-display text-2xl font-bold" data-testid="approval-total">{da(q.totals.ttc)}</span>
      </div>
    </div>
  );
}

function DecisionForm({ token, onDone }: { token: string; onDone: (s: string) => void }) {
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const send = async (decision: string) => {
    if (name.trim().length < 2) return setErr("Indiquez votre nom complet pour confirmer");
    setBusy(decision); setErr("");
    try {
      await axios.post(`${API}/public/quote/${token}/decision`, { decision, by_name: name, note });
      onDone(decision);
    } catch (e) { setErr(errMsg(e)); }
    setBusy("");
  };
  return (
    <div className="mt-6 space-y-3" data-testid="approval-form">
      <label className="eyebrow block">Votre nom</label>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom et prénom" className="h-12" data-testid="approval-name-input" />
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Remarque (facultatif)" data-testid="approval-note-input" />
      {err && <div className="text-sm text-red-400" data-testid="approval-error">{err}</div>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Button className="h-12 bg-emerald-600 text-white hover:bg-emerald-500" disabled={!!busy} onClick={() => send("accepte")} data-testid="approval-accept-btn">
          {busy === "accepte" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}J'accepte le devis
        </Button>
        <Button variant="outline" className="h-12" disabled={!!busy} onClick={() => send("refuse")} data-testid="approval-refuse-btn">
          {busy === "refuse" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}Je refuse
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">En acceptant, vous autorisez le garage à réaliser les travaux de ce devis. Votre nom, la date et l'heure sont enregistrés.</p>
    </div>
  );
}

export default function QuoteApproval() {
  const { token = "" } = useParams();
  const [q, setQ] = useState<any>(null);
  const [error, setError] = useState("");
  const load = () => axios.get(`${API}/public/quote/${token}`).then((r) => setQ(r.data)).catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen bg-background px-4 py-10" data-testid="quote-approval-page">
      <div className="mx-auto max-w-xl">
        <Logo variant="premium" custom={false} className="mx-auto mb-8 w-56" />
        {error && <div className="plate rounded-sm p-6 text-center text-sm" data-testid="approval-invalid">{error}</div>}
        {!q && !error && <div className="flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>}
        {q && (
          <div className="plate rounded-sm p-6 fade-up">
            <div className="eyebrow">Devis {q.number} · version {q.version}</div>
            <h1 className="mt-2 font-display text-2xl font-bold uppercase sm:text-3xl">Bonjour {q.client_name}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Véhicule <span className="font-mono-data text-foreground">{q.vehicle.plate}</span> {q.vehicle.brand} {q.vehicle.model} · valable jusqu'au {q.valid_until.split("-").reverse().join("/")}
            </p>
            {q.diagnosis && <p className="mt-4 text-sm">{q.diagnosis}</p>}
            <Lines q={q} />
            <a href={`${API}/public/doc/${token}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm underline-offset-4 hover:underline" data-testid="approval-pdf-link">
              <FileText className="h-4 w-4" />Voir le devis détaillé (PDF)
            </a>
            {q.status === "envoye" ? <DecisionForm token={token} onDone={() => load()} /> : (
              <div className="mt-6 rounded-sm border border-border p-4 text-sm" data-testid="approval-status">
                {MSG[q.status] || "Ce devis n'est plus modifiable."}
                {q.decision && <div className="mt-1 text-xs text-muted-foreground">{q.decision.by_name} · {fmtDate(q.decision.at)}</div>}
              </div>
            )}
            {q.garage.phone && (
              <a href={`tel:${q.garage.phone}`} className="mt-6 flex items-center gap-2 text-xs text-muted-foreground" data-testid="approval-garage-phone">
                <Phone className="h-3.5 w-3.5" />{q.garage.name} · {q.garage.phone}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
